"use client";

import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "@/src/components/Home/LandingV6/landing-v6.css";
import { Nav, Button, QuarklePlanet } from "@/src/components/Home/LandingV6/ui";
import { Footer } from "@/src/components/Home/LandingV6/blocks";
import {
  Sparkle,
  Star,
  WaveRule,
  Ribbon,
  TapeSticker,
  CornerPin,
  OutlineDisplay,
  BaselineGrid,
  Asterism,
} from "@/src/components/Home/LandingV6/ornaments";

const TEAM_EMAIL = "team@quarkle.ai";

const CATEGORIES = [
  { k: "feature", label: "a wish for a feature", accent: "var(--pink)" },
  { k: "bug", label: "something's broken", accent: "var(--clay)" },
  { k: "praise", label: "a kind word", accent: "var(--lime)" },
  { k: "other", label: "something else", accent: "var(--sky)" },
];

function encodeMailto({ name, email, category, message }) {
  const catLabel = CATEGORIES.find((c) => c.k === category)?.label || "note";
  const subject = `Quarkle feedback · ${catLabel}`;
  const bodyLines = [
    `Hi Quarkle team,`,
    ``,
    message || "",
    ``,
    `--`,
    name ? `From: ${name}` : null,
    email ? `Reply-to: ${email}` : null,
    `Category: ${catLabel}`,
  ].filter((l) => l !== null);
  const body = bodyLines.join("\n");
  return `mailto:${TEAM_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function FeedbackPage() {
  const [theme, setTheme] = useState("paper");
  const { user, isAuthenticated } = useAuth0();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("feature");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      const s = window.localStorage.getItem("qv6-theme");
      if (s === "paper" || s === "midnight") setTheme(s);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      if (!name && user.name) setName(user.name);
      if (!email && user.email) setEmail(user.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const canSend = message.trim().length >= 4 && !honeypot;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!canSend) return;
    const href = encodeMailto({ name: name.trim(), email: email.trim(), category, message: message.trim() });
    window.location.href = href;
    setSent(true);
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(TEAM_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {}
  };

  const activeAccent = CATEGORIES.find((c) => c.k === category)?.accent || "var(--pink)";

  return (
    <div className={`qv6 ${theme === "midnight" ? "theme-midnight" : ""}`}>
      <Nav />

      {/* Hero */}
      <section style={{ padding: "70px 40px 30px", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{ position: "absolute", top: 40, right: -30, opacity: 0.1, pointerEvents: "none" }}>
          <OutlineDisplay text="dear team," size={260} color="var(--pink)" strokeW={1.8} />
        </div>
        <div aria-hidden style={{ position: "absolute", bottom: 10, left: 60 }}>
          <Asterism size={24} color="var(--clay)" />
        </div>
        <div aria-hidden style={{ position: "absolute", top: 90, right: 140 }}>
          <Sparkle size={32} color="var(--pink)" style={{ animation: "qv6-sparkle 3s infinite" }} />
        </div>
        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", textAlign: "center" }}>
          <div style={{ display: "inline-flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
            <WaveRule w={60} color="var(--ink)" />
            <span className="caps" style={{ color: "var(--pink)" }}>feedback · open mailbox</span>
            <WaveRule w={60} color="var(--ink)" />
          </div>
          <h1
            className="tanker"
            style={{
              fontSize: "clamp(56px, 8.2vw, 140px)",
              lineHeight: 0.88,
              letterSpacing: "-0.04em",
              margin: "0 0 18px",
            }}
          >
            write us a <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>letter</span>.
          </h1>
          <p className="display" style={{ fontSize: 22, fontStyle: "italic", opacity: 0.85, maxWidth: 720, margin: "0 auto 22px", lineHeight: 1.5 }}>
            a real person on our team reads every note, usually within a day. tell us what's working, what's broken, what you wish we'd build next.
          </p>
          <div style={{ display: "inline-flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
            <CornerPin>posted straight to {TEAM_EMAIL}</CornerPin>
          </div>
        </div>
      </section>

      {/* Letter card */}
      <section style={{ padding: "30px 40px 80px", position: "relative" }}>
        <div style={{ maxWidth: 820, margin: "0 auto", position: "relative" }}>
          {/* Ribbon */}
          <div style={{ position: "absolute", top: -16, left: 28, zIndex: 2 }}>
            <Ribbon color={activeAccent} rotate={-4} fg={category === "praise" ? "var(--ink)" : "#fff"}>
              {CATEGORIES.find((c) => c.k === category)?.label}
            </Ribbon>
          </div>

          {!sent ? (
            <form
              onSubmit={onSubmit}
              style={{
                background: "var(--paper-3)",
                border: "1.5px solid var(--ink)",
                borderRadius: 14,
                padding: "44px 36px 32px",
                boxShadow: `10px 10px 0 ${activeAccent}`,
                position: "relative",
                transition: "box-shadow 0.3s ease",
              }}
            >
              {/* paper punch holes */}
              <span aria-hidden style={{ position: "absolute", left: -8, top: "22%", width: 14, height: 14, borderRadius: 999, background: "var(--paper)", border: "1px solid var(--ink)" }} />
              <span aria-hidden style={{ position: "absolute", left: -8, top: "55%", width: 14, height: 14, borderRadius: 999, background: "var(--paper)", border: "1px solid var(--ink)" }} />
              <span aria-hidden style={{ position: "absolute", left: -8, top: "85%", width: 14, height: 14, borderRadius: 999, background: "var(--paper)", border: "1px solid var(--ink)" }} />

              {/* Category pickers */}
              <div style={{ marginBottom: 22 }}>
                <label className="caps" style={{ opacity: 0.6, display: "block", marginBottom: 10 }}>
                  what's this about
                </label>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {CATEGORIES.map((c) => {
                    const on = category === c.k;
                    return (
                      <button
                        key={c.k}
                        type="button"
                        onClick={() => setCategory(c.k)}
                        className="mono"
                        style={{
                          all: "unset",
                          cursor: "pointer",
                          padding: "9px 16px",
                          border: "1.5px solid var(--ink)",
                          borderRadius: 999,
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: "0.14em",
                          background: on ? c.accent : "var(--paper)",
                          color: on && c.k !== "praise" ? "#fff" : "var(--ink)",
                          boxShadow: on ? `3px 3px 0 var(--ink)` : `2px 2px 0 var(--ink)`,
                          transform: on ? "translate(-1px, -1px)" : "none",
                          transition: "all 0.18s ease",
                        }}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Name + email */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="qv6-feedback-grid">
                <Field
                  label="your name"
                  optional
                  value={name}
                  onChange={setName}
                  placeholder="so we know who to thank"
                  autoComplete="name"
                />
                <Field
                  label="reply-to email"
                  optional
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder="only if you want us to write back"
                  autoComplete="email"
                />
              </div>

              {/* Honeypot (hidden) */}
              <div aria-hidden style={{ position: "absolute", left: -9999, top: -9999, height: 0, overflow: "hidden" }}>
                <label>
                  leave this empty
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </label>
              </div>

              {/* Message */}
              <div style={{ marginBottom: 20 }}>
                <label className="caps" style={{ opacity: 0.6, display: "block", marginBottom: 8 }}>
                  your note
                </label>
                <textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    category === "bug"
                      ? "what happened, what you expected, and (if it helps) what book or chapter you were in when it broke."
                      : category === "feature"
                      ? "describe it like a scene: what were you trying to do, what did you reach for, what would've helped?"
                      : category === "praise"
                      ? "go on. our cold little hearts need it."
                      : "tell us anything. we're listening."
                  }
                  rows={8}
                  style={{
                    width: "100%",
                    resize: "vertical",
                    minHeight: 180,
                    padding: "16px 18px",
                    background: "var(--paper)",
                    color: "var(--ink)",
                    border: "1.5px solid var(--ink)",
                    borderRadius: 8,
                    fontSize: 16,
                    fontFamily: "'Gambarino', 'DM Serif Display', serif",
                    lineHeight: 1.55,
                    outline: "none",
                    boxShadow: `inset 0 0 0 3px var(--paper-3)`,
                  }}
                />
                <div className="mono" style={{ marginTop: 8, fontSize: 10, opacity: 0.55, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                  {message.trim().length} chars · write as little or as much as you want
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
                <Button
                  type="submit"
                  variant="pink"
                  size="lg"
                  disabled={!canSend}
                  style={{
                    opacity: canSend ? 1 : 0.45,
                    cursor: canSend ? "pointer" : "not-allowed",
                  }}
                >
                  send the letter →
                </Button>
                <button
                  type="button"
                  onClick={copyEmail}
                  className="mono"
                  style={{
                    all: "unset",
                    cursor: "pointer",
                    fontSize: 12,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    opacity: 0.7,
                    borderBottom: "1px dashed var(--rule)",
                    paddingBottom: 2,
                  }}
                >
                  {copied ? "copied · " : "or copy · "}{TEAM_EMAIL}
                </button>
              </div>

              {/* Footnote */}
              <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px dashed var(--rule)" }}>
                <span className="mono" style={{ fontSize: 10, opacity: 0.6, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                  your words aren't sent to a server · send opens your mail app so you see exactly what we see
                </span>
              </div>
            </form>
          ) : (
            <SentCard
              onReset={() => {
                setSent(false);
                setMessage("");
              }}
              onCopy={copyEmail}
              copied={copied}
            />
          )}

          {/* Side-quest strip */}
          <div style={{ marginTop: 44, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18 }} className="qv6-feedback-side">
            <SideCard
              title="on fire, can't wait"
              body="urgent or sensitive? email us direct. we'll reply the same day."
              cta={{ label: TEAM_EMAIL, href: `mailto:${TEAM_EMAIL}` }}
              accent="var(--clay)"
            />
            <SideCard
              title="company of writers"
              body="join the discord if you want to hang out with other folks using quarkle."
              cta={{ label: "open discord →", href: "https://discord.gg/cPfBDmZNM2", external: true }}
              accent="var(--sky)"
            />
            <SideCard
              title="the fine print"
              body="we never sell your notes, never train on your words, and won't surprise you."
              cta={{ label: "read the privacy policy →", href: "/privacy" }}
              accent="var(--moss-2)"
            />
          </div>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        @media (max-width: 760px) {
          :global(.qv6-feedback-grid) {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, optional, autoComplete }) {
  return (
    <label style={{ display: "block" }}>
      <span className="caps" style={{ opacity: 0.6, display: "block", marginBottom: 8 }}>
        {label} {optional && <span style={{ opacity: 0.45 }}>· optional</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={{
          width: "100%",
          padding: "13px 16px",
          background: "var(--paper)",
          color: "var(--ink)",
          border: "1.5px solid var(--ink)",
          borderRadius: 8,
          fontSize: 15,
          fontFamily: "inherit",
          outline: "none",
          boxShadow: `inset 0 0 0 3px var(--paper-3)`,
        }}
      />
    </label>
  );
}

function SentCard({ onReset, onCopy, copied }) {
  return (
    <div
      style={{
        background: "var(--ink)",
        color: "var(--paper)",
        border: "1.5px solid var(--ink)",
        borderRadius: 14,
        padding: "48px 36px 36px",
        boxShadow: "10px 10px 0 var(--pink)",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <BaselineGrid w={1600} h={400} cell={48} color="var(--paper)" opacity={0.07} />
      </div>
      <div style={{ position: "relative" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <QuarklePlanet size={40} color="var(--pink)" ring="var(--paper-3)" />
          <span className="caps" style={{ color: "var(--lime)" }}>letter in the post</span>
        </div>
        <h2 className="tanker" style={{ fontSize: "clamp(40px, 5.2vw, 72px)", margin: "0 0 14px", lineHeight: 0.94 }}>
          off it <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>goes</span>.
        </h2>
        <p className="display" style={{ fontSize: 19, fontStyle: "italic", opacity: 0.88, maxWidth: 560, margin: "0 auto 22px", lineHeight: 1.55 }}>
          your mail app should have opened with the letter ready to send. if nothing happened, your browser might be blocking mailto. you can copy the address and write us manually.
        </p>
        <div style={{ display: "inline-flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
          <Button variant="pink" size="lg" onClick={onReset}>write another →</Button>
          <button
            type="button"
            onClick={onCopy}
            className="mono"
            style={{
              all: "unset",
              cursor: "pointer",
              fontSize: 12,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              opacity: 0.8,
              borderBottom: "1px dashed var(--rule)",
              paddingBottom: 2,
              color: "var(--paper)",
            }}
          >
            {copied ? "copied · " : "copy · "}{TEAM_EMAIL}
          </button>
        </div>
      </div>
    </div>
  );
}

function SideCard({ title, body, cta, accent }) {
  return (
    <div
      style={{
        background: "var(--paper-2)",
        border: "1.5px solid var(--ink)",
        borderRadius: 12,
        padding: "22px 22px 20px",
        boxShadow: "6px 6px 0 var(--ink)",
        position: "relative",
      }}
    >
      <div aria-hidden style={{ position: "absolute", top: 12, right: 14, width: 10, height: 10, borderRadius: 999, background: accent, boxShadow: `0 0 0 3px var(--paper-2), 0 0 0 4px var(--ink)` }} />
      <h3 className="tanker" style={{ fontSize: 22, margin: "0 0 8px", lineHeight: 1.05 }}>{title}</h3>
      <p className="display" style={{ margin: "0 0 14px", fontSize: 15, lineHeight: 1.5, fontStyle: "italic", opacity: 0.85 }}>{body}</p>
      <a
        href={cta.href}
        target={cta.external ? "_blank" : undefined}
        rel={cta.external ? "noreferrer" : undefined}
        className="mono"
        style={{
          fontSize: 11,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--ink)",
          textDecoration: "none",
          borderBottom: "1.5px solid var(--ink)",
          paddingBottom: 2,
        }}
      >
        {cta.label}
      </a>
    </div>
  );
}
